import { fetchMarketTargets } from "api/targets"
import { BaseRecord } from "components/Cable"
import { MarketTarget } from "models/targets"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { queryCache, useQuery } from "react-query"
import { NON_FILTERABLE } from "./utils"
import { omit, mergeWith } from "lodash"

export function useBaseRateFilters<T extends BaseRecord>(
  data?: T[],
  fieldFilters?: string[],
) {
  const { t } = useI18n()
  const { meta } = useAuthenticated()
  const marketId = meta?.layerId ?? ""

  const cachedTargets = queryCache.getQueryData<MarketTarget[]>([
    "targets",
    marketId,
  ])

  const { data: availableTargets } = useQuery(
    ["targets", marketId],
    fetchMarketTargets,
    {
      enabled: !cachedTargets,
      initialData: cachedTargets,
      refetchOnMount: !cachedTargets,
    },
  )

  const _data = data?.map((d) => omit(d, [...NON_FILTERABLE, "timebelt"])) ?? []

  const mergedOptions = mergeWith(
    {},
    ..._data,
    (value: string[], nextValue: string[]) => (value || []).concat(nextValue),
  )

  const options = Object.assign(
    {},
    ...Object.keys(mergedOptions).map((k) => ({
      [k]: [...new Set(mergedOptions?.[k])]?.map((o) => {
        switch (k) {
          case "daysOfWeek":
            return {
              label: t(`days.${o}`),
              value: o,
            }
          case "targetB": {
            return {
              label: availableTargets?.find((t) => t?.id === o)?.name,
              value: o,
            }
          }
          default: {
            return {
              value: o,
              label: o,
            }
          }
        }
      }),
    })),
  )

  return {
    baseRateFilters: fieldFilters?.map((f) => ({
      name: f,
      options: options?.[f],
    })),
  }
}
